import { formsContent } from '../../content/forms';

/**
 * The one button that submits a signup.
 *
 * Same geometry as `ButtonLink` — full width on a phone, comfortably past the
 * 44px minimum target, visible focus ring — because a visitor should not have
 * to work out that this control is different from the ones that brought them
 * here (spec section 11).
 *
 * While a submission is in flight the button is disabled and its label changes.
 * The label change is the accessible status too: `aria-busy` alone is not
 * announced reliably, and the visible text is what everyone can see.
 */
export interface SubmitButtonProps {
  label: string;
  pending: boolean;
  /** `accent` is the provider path's utility green (spec section 11). */
  variant?: 'primary' | 'accent';
}

const variantClasses = {
  primary: 'bg-ink text-field hover:bg-ink/90 focus-visible:outline-ink',
  accent: 'bg-utility text-field hover:bg-utility/90 focus-visible:outline-utility',
} as const;

export function SubmitButton({ label, pending, variant = 'primary' }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={pending}
      aria-busy={pending || undefined}
      className={[
        'inline-flex min-h-12 w-full items-center justify-center rounded-md px-6 py-3',
        'text-base font-semibold transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2',
        'disabled:cursor-progress disabled:opacity-70',
        variantClasses[variant],
        'sm:w-auto',
      ].join(' ')}
    >
      {pending ? formsContent.common.submittingLabel : label}
    </button>
  );
}
